import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title = "Arvix Creative - Creative Studio | Brand Identity, UI/UX, Web Development",
  description = "Professional creative studio specializing in brand identity, UI/UX design, web development, and visual solutions. 120+ projects completed with 85+ happy clients and 5+ years of experience. Based in Chattogram, Bangladesh.",
  keywords = "arvix, arvix creative, creative studio, brand identity, UI/UX design, web development, logo design, branding, visual solutions, Bangladesh, Chattogram, tanvirus, tanvir, pixels, tanvirus pixels",
  image = "https://arvixcreative.com/logo512.png",
  url = "https://arvixcreative.com",
  type = "website",
  structuredData = null,
  noindex = false,
  nofollow = false
}) => {
  const fullTitle = title.includes("Arvix Creative") ? title : `${title} | Arvix Creative`;
  const fullImage = image.startsWith('http') ? image : new URL(image, url).href;
  const robotsContent = noindex || nofollow 
    ? `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:secure_url" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content="Arvix Creative" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="en_BD" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:image:alt" content={fullTitle} />
      <meta name="twitter:site" content="@arvixcreative" />
      <meta name="twitter:creator" content="@arvixcreative" />

      {/* Additional SEO Meta Tags */}
      <meta name="robots" content={robotsContent} />
      <meta name="author" content="Arvix Creative" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="coverage" content="worldwide" />
      <meta name="target" content="all" />
      <meta name="audience" content="all" />
      <meta name="theme-color" content="#FF5E5B" />
      
      {/* Geographic Meta Tags */}
      <meta name="geo.region" content="BD-10" />
      <meta name="geo.placename" content="Chattogram" />
      <meta name="geo.position" content="22.3569;91.7832" />
      <meta name="ICBM" content="22.3569, 91.7832" />

      {/* Mobile Optimization */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="Arvix Creative" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
